// src/pages/UserList.jsx
// CRUD de usuarios del sistema (WHL_USR_USUARIO)

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { usersApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTexts, TXT } from '../context/TextContext';
import { useToast } from '../context/ToastContext';
import { formatDate } from '../utils/formatDate';
import UserModal from '../components/modals/UserModal';

export default function UserList() {
  const navigate             = useNavigate();
  const { login: authLogin, user } = useAuth();
  const canImpersonate = (user?.functions || []).includes(37);
  const { t, idiId }         = useTexts();
  const { showToast }        = useToast();

  const [users, setUsers]       = useState([]);
  const [groups, setGroups]     = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const searchTimer = useRef(null);

  // Modal state
  const [showModal, setShowModal]   = useState(false);
  const [modalData, setModalData]   = useState(null); // null = new, object = edit

  useEffect(() => {
    load();
    usersApi.groups().then(r => setGroups(r.data)).catch(() => {});
    usersApi.profiles().then(r => setProfiles(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const handleFilter = (e) => navigate('/documents', { state: { filterId: e.detail?.filterId } });
    const handleSearch = (e) => navigate('/documents', { state: { search: e.detail?.text } });
    window.addEventListener('whl:filter', handleFilter);
    window.addEventListener('whl:search', handleSearch);
    return () => {
      window.removeEventListener('whl:filter', handleFilter);
      window.removeEventListener('whl:search', handleSearch);
    };
  }, [navigate]);

  const load = async (q = '') => {
    setLoading(true);
    try {
      const res = await usersApi.list(q || undefined);
      setUsers(res.data);
    } catch (err) {
      console.error('Users load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(val), 350);
  };

  // ── Toggle habilitar/deshabilitar ────────────────────────────────────────
  const handleToggle = async (u) => {
    try {
      await usersApi.toggle(u.Usr_IdUsr);
      setUsers(prev => prev.map(x =>
        x.Usr_IdUsr === u.Usr_IdUsr
          ? { ...x, Usr_Habilitado: (x.Usr_Habilitado === 'Y' || x.Usr_Habilitado === 'S') ? 'N' : 'Y' }
          : x
      ));
    } catch (err) {
      showToast(err.response?.data?.error || t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
    }
  };

  // ── Abrir modal ──────────────────────────────────────────────────────────
  const openNew  = () => { setModalData(null);  setShowModal(true); };
  const openEdit = (u) => { setModalData(u);    setShowModal(true); };
  const closeModal = () => setShowModal(false);

  const handleSaved = ({ isNew, clave, id, form }) => {
    if (isNew) {
      load(search);
      if (clave) showToast(`${t(TXT.USER_CREATED_PWD, 'Usuario creado. Clave inicial:')} ${clave}`, 'success');
    } else {
      setUsers(prev => prev.map(x =>
        x.Usr_IdUsr === id
          ? {
              ...x,
              Usr_Login:    form.login,
              Usr_SMTP:     form.smtp || form.login,
              Usr_ApeyNom:  form.apeyNom,
              Usr_Cuil:     form.cuil,
              Usr_idioma:   form.idioma,
              Usr_Timezone: form.timezone,
              Grp_IdGrp:      form.grpId ? parseInt(form.grpId) : x.Grp_IdGrp,
              Per_IdPer:      form.perId ? parseInt(form.perId) : null,
              Usr_Habilitado: form.habilitado ? 'Y' : 'N',
            }
          : x
      ));
      showToast(t(TXT.DATA_UPDATED, 'Datos actualizados'), 'success');
    }
  };

  // ── Impersonar ───────────────────────────────────────────────────────────
  const handleImpersonate = async (u) => {
    try {
      const res = await usersApi.impersonate(u.Usr_IdUsr);
      const { token, user: impUser } = res.data;
      localStorage.setItem('whl_token_original', localStorage.getItem('whl_token'));
      localStorage.setItem('whl_user_original',  localStorage.getItem('whl_user'));
      localStorage.setItem('whl_token', token);
      localStorage.setItem('whl_user',  JSON.stringify(impUser));
      window.location.href = '/documents';
    } catch (err) {
      showToast(err.response?.data?.error || t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
    }
  };

  // ── Welcome mail ─────────────────────────────────────────────────────────
  const handleWelcomeMail = async (u) => {
    try {
      await usersApi.welcomeMail(u.Usr_IdUsr);
      setUsers(prev => prev.map(x => x.Usr_IdUsr === u.Usr_IdUsr ? { ...x, Usr_Welcome: new Date().toISOString() } : x));
      showToast(t(TXT.WELCOME_MAIL_SENT, 'Mail enviado'), 'success');
    } catch (err) {
      showToast(err.response?.data?.error || t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
    }
  };

  // ── Magic link ───────────────────────────────────────────────────────────
  const handleMagicLink = async (u) => {
    try {
      const res = await usersApi.magicLink(u.Usr_IdUsr, 7);
      await navigator.clipboard.writeText(res.data.link);
      showToast(t(TXT.LINK_COPIED, 'Link copiado al portapapeles'), 'success');
    } catch (err) {
      showToast(err.response?.data?.error || t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
    }
  };

  const isEnabled = (u) => u.Usr_Habilitado === 'Y' || u.Usr_Habilitado === 'S';

  const buttonBar = (
    <div className="d-flex align-items-center gap-2">
      <button className="button color1" onClick={openNew}>
        <i className="fa-solid fa-plus fa-1x color1" />&nbsp;&nbsp;{t(TXT.NEW, 'Nuevo')}&nbsp;&nbsp;
      </button>
      <input
        type="text"
        value={search}
        onChange={e => handleSearch(e.target.value)}
        placeholder={t(TXT.SEARCH, 'Buscar') + '…'}
        style={{ padding: '3px 8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.875rem', width: '220px' }}
      />
      <i className="fa-solid fa-magnifying-glass" style={{ color: '#888', cursor: 'pointer' }} onClick={() => load(search)} />
    </div>
  );

  return (
    <Layout buttonBar={buttonBar}>
      {loading ? (
        <div className="text-center mt-5"><div className="spinner-border text-secondary" /></div>
      ) : (
        <table className="table table-sm table-hover mt-2" style={{ fontSize: '0.875rem' }}>
          <thead>
            <tr>
              <th>{t(TXT.USUARIO_LABEL, 'Usuario')}</th>
              <th>{t(TXT.APELLIDO_NOMBRE, 'Apellido y Nombre')}</th>
              <th>{t(TXT.MAIL_LABEL, 'Mail')}</th>
              <th>{t(TXT.CUIL, 'CUIL')}</th>
              <th>{t(TXT.GRUPO_USUARIOS, 'Grupo de usuarios')}</th>
              <th>{t(TXT.WELCOME, 'Bienvenida')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const enabled = isEnabled(u);

              return (
                <tr key={u.Usr_IdUsr} style={{ verticalAlign: 'middle' }}>

                  <td>{u.Usr_Login}</td>
                  <td>{u.Usr_ApeyNom}</td>
                  <td>{u.Usr_SMTP}</td>
                  <td>{u.Usr_Cuil}</td>
                  <td>{groups.find(g => g.Grp_IdGrp === u.Grp_IdGrp)?.Grp_DesGrp || u.Grp_IdGrp}</td>

                  <td style={{ color: u.Usr_Welcome ? '#555' : '#ccc', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                    {formatDate(u.Usr_Welcome, user?.timezone, idiId)}
                  </td>

                  <td className="text-end">
                    <div className="d-flex justify-content-end gap-1">

                      {/* Habilitar / Deshabilitar */}
                      <button
                        className="btn btn-light btn-sm"
                        title={enabled ? t(TXT.DISABLE_USER, 'Deshabilitar') : t(TXT.ENABLE_USER, 'Habilitar')}
                        onClick={() => handleToggle(u)}
                      >
                        <i className={`fa-solid fa-1x ${enabled ? 'fa-ban' : 'fa-circle-check'}`}
                           style={{ color: enabled ? '#dc3545' : '#2a9d8f' }} />
                      </button>

                      {/* Editar */}
                      <button className="btn btn-light btn-sm" title={t(TXT.UPDATE, 'Modificar')} onClick={() => openEdit(u)}>
                        <i className="fa-solid fa-pen fa-1x" style={{ color: '#555' }} />
                      </button>

                      {/* Mail de bienvenida */}
                      <button className="btn btn-light btn-sm" title="Mail de bienvenida" onClick={() => handleWelcomeMail(u)}>
                        <i className="fa-solid fa-envelope fa-1x" style={{ color: '#555' }} />
                      </button>

                      {/* Magic link */}
                      <button className="btn btn-light btn-sm" title="Generar link de acceso directo (7 días)" onClick={() => handleMagicLink(u)}>
                        <i className="fa-solid fa-link fa-1x" style={{ color: '#555' }} />
                      </button>

                      {/* Impersonar */}
                      {canImpersonate && (
                        <button className="btn btn-light btn-sm" title="Impersonar" onClick={() => handleImpersonate(u)}>
                          <i className="fa-solid fa-user-secret fa-1x" style={{ color: '#555' }} />
                        </button>
                      )}

                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <UserModal
        show={showModal}
        userData={modalData}
        groups={groups}
        profiles={profiles}
        onClose={closeModal}
        onSaved={handleSaved}
      />
    </Layout>
  );
}
